import { useEffect, useRef, useState } from 'react'

import { PhnxButton } from '../../01-atoms/Button/Button'
import { PhnxHubspotform } from '../../01-atoms/Hubspotform/Hubspotform'
import { PhnxOfficeDetails } from '../../02-molecules/OfficeDetails/OfficeDetails'
import { PhnxImageslider } from '../../03-organisms/Imageslider/Imageslider'
import { PhnxImageSliderCardEndorsement } from '../../03-organisms/Imageslider/ImageSliderCardEndorsement'
import styles from './AdviserPage.module.css'

export type officeData = {
  title: string
  address: string
  phone: string
  link?: string
  location: {
    lat: number
    long: number
  }
}

export type endorsementData = {
  description: string
  name: string
  position: string
  stars: number
}

export type adviserPageProps = {
  title: string
  header?: React.ReactNode
  biography?: React.ReactNode
  qualifications?: React.ReactNode
  officelocations?: officeData[]
  articles?: React.ReactNode
  endorsements?: endorsementData[]
}

export const PhnxAdviserPage: React.FC<adviserPageProps> = ({
  title,
  header,
  biography,
  qualifications,
  officelocations,
  articles,
  endorsements,
}) => {
  const [isDesktop, setIsDesktop] = useState(false)
  const [isTablet, setIsTablet] = useState(false)
  const containerDivRef = useRef<HTMLDivElement>(null)
  const rightDivRef = useRef<HTMLDivElement>(null)
  const isNZ = process.env.NEXT_PUBLIC_NEXT_LOCATION === 'en-nz'
  const formRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth
      setIsDesktop(width >= 1280)
      setIsTablet(width >= 768 && width < 1280)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Use scroll position and footer detection to manage button state
  useEffect(() => {
    if (isDesktop || isTablet) return

    const handleScroll = () => {
      const windowHeight = window.innerHeight
      const documentHeight = document.documentElement.scrollHeight
      const scrollTop = window.scrollY
      const footer = document.querySelector('footer')

      if (footer) {
        const footerTop = footer.offsetTop
        const scrollBottom = scrollTop + windowHeight

        // If we're close to or past the footer, make button static
        if (scrollBottom >= footerTop - 50) {
          setIsButtonFixed(false)
        } else {
          setIsButtonFixed(true)
        }
      } else {
        // Fallback: use document height if no footer found
        if (scrollTop + windowHeight >= documentHeight - 100) {
          setIsButtonFixed(false)
        } else {
          setIsButtonFixed(true)
        }
      }
    }

    handleScroll() // Run once on mount
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isDesktop, isTablet])

  const [isFormVisible, setIsFormVisible] = useState<boolean>(false)
  const [isButtonFixed, setIsButtonFixed] = useState<boolean>(true)

  const showForm = () => {
    document.body.style.overflow = 'hidden'
    setIsFormVisible(true)
  }

  const hideForm = () => {
    document.body.style.overflow = ''
    setIsFormVisible(false)
  }

  // Get current location from environment variable
  const location = process.env.NEXT_PUBLIC_NEXT_LOCATION || 'default'
  const isNZLocale = location === 'en-nz'

  return (
    <div className={styles.adviserPage}>
      <h1 className="sr-only">Advisor: {title}</h1>
      <div className={styles.adviserHeaderWrapper}>
        <div className={styles.adviserHeader} ref={containerDivRef}>
          <div className={styles.adviserDetails}>{header ?? null}</div>
          {isDesktop && !isTablet && (
            <div
              ref={rightDivRef}
              style={{
                width: '393px',
                minWidth: '393px',
                maxWidth: '393px',
                padding: '1rem',
                position: 'relative',
              }}
            ></div>
          )}
        </div>
      </div>
      <div className={styles.adviserBody}>
        <div className={styles.adviserBodyContent}>
          {biography != null && (
            <div className={styles.adviserBiography}>
              <h2>Biography</h2>
              {biography}
            </div>
          )}
          {qualifications != null && (
            <div className={styles.adviserQualifications}>
              <h3>Qualifications and Professional Memberships</h3>
              {qualifications}
            </div>
          )}
          {officelocations != null && officelocations.length > 0 && (
            <div className={styles.adviserLocations}>
              <h3>Office locations</h3>
              <div className={styles.advisorLocationsOffices}>
                {officelocations.map((item: officeData, index: number) => (
                  <div key={'office' + index}>
                    <PhnxOfficeDetails
                      formId={''}
                      form={undefined}
                      mailingAddress={''}
                      variant="card"
                      title={item.title}
                      address={item.address}
                      phone={item.phone}
                      link={item.link}
                      hours=""
                      location={item.location}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        {isDesktop && !isTablet && (
          <div
            ref={formRef}
            style={{
              width: '393px',
              minWidth: '393px',
              maxWidth: '393px',
              marginTop: '-15rem',
              zIndex: 10,
            }}
          >
            <div className={styles.formHeader}>
              <div className={styles.formTitle}>{`Contact ${title.split(' ')[0]}'s Team`}</div>
              <div className={styles.formBody}>
                {isNZ ? (
                  'Please fill out the form and we will be in touch.'
                ) : (
                  <>
                    Please fill out the form and we will be in touch, or phone us on{' '}
                    <a href="tel:1300 856 065">1300 856 065.</a>
                  </>
                )}
              </div>
            </div>
            <div className={styles.formInclude}>
              <PhnxHubspotform
                title={`Contact ${title.split(' ')[0]}'s Team`}
                formId={
                  isNZLocale
                    ? '2f3e35b1-fa16-4cb7-98c5-596d6ed1e754'
                    : '51e025c4-8223-409e-9520-973f57831d5e'
                }
                hiddenFields={{ '0-1/last_submitted_servicing_adviser_name': title }}
              />
            </div>
          </div>
        )}
      </div>
      {isTablet && (
        <div className={styles.adviserFormTablet}>
          <div className={styles.formHeader}>
            <div className={styles.formTitle}>{`Contact ${title.split(' ')[0]}'s Team`}</div>
            <div className={styles.formBody}>
              {isNZ ? (
                'Please fill out the form and we will be in touch.'
              ) : (
                <>
                  Please fill out the form and we will be in touch, or phone us on{' '}
                  <a href="tel:1300 856 065">1300 856 065.</a>
                </>
              )}
            </div>
          </div>
          <div className={styles.formInclude}>
            <PhnxHubspotform
              title={`Contact ${title.split(' ')[0]}'s Team`}
              formId={
                isNZLocale
                  ? '2f3e35b1-fa16-4cb7-98c5-596d6ed1e754'
                  : '51e025c4-8223-409e-9520-973f57831d5e'
              }
              hiddenFields={{ '0-1/last_submitted_servicing_adviser_name': title }}
            />
          </div>
        </div>
      )}
      {endorsements != null && endorsements.length > 0 && (
        <div className={styles.adviserEndorsementsContainer}>
          <div className={styles.adviserEndorsements}>
            <h3>Client endorsements</h3>
            <PhnxImageslider
              variant="endorsement"
              items={endorsements.map((item, index) => (
                <PhnxImageSliderCardEndorsement
                  key={'endorsement' + index}
                  itemKey={'endorsement' + index}
                  stars={item.stars}
                  description={item.description}
                  name={item.name}
                  position={item.position}
                />
              ))}
              autoplay={false}
              backgroundColor={'#FFFFFF'}
              textColor={'#000'}
            />
          </div>
        </div>
      )}
      {articles != null && (
        <div className={styles.adviserArticlesBackground}>
          <div className={styles.adviserArticlesContainer}>
            <div className={styles.adviserArticles}>
              <h3 className={styles.adviserArticlesTitle}>Articles by {title.split(' ')[0]}</h3>
              <PhnxImageslider
                variant="article"
                items={articles as React.ReactNode[]}
                autoplay={false}
                backgroundColor={'#F4F4F4'}
                textColor={'#000'}
              />
            </div>
          </div>
        </div>
      )}
      {!isDesktop && !isTablet && (
        <>
          <div className={styles.advisorButtonContainer}>
            <div
              className={`${styles.advisorButton} ${isButtonFixed ? styles.advisorButtonFixed : styles.advisorButtonStatic}`}
            >
              <PhnxButton
                type="button"
                variant="primary"
                size="default"
                label={`Contact ${title.split(' ')[0]}'s Office`}
                disabled={false}
                onClick={showForm}
              />
            </div>
          </div>
          <div
            className={`${styles.adviserFormMobile} ${isFormVisible ? styles.formVisible : styles.formHidden}`}
          >
            <div className={styles.advisorFormClose}>
              <PhnxButton
                type="button"
                variant="quaternary"
                size="small"
                label={'Close'}
                disabled={false}
                onClick={hideForm}
                icon={'fx-icon-dismiss-lg'}
                iconPosition={'right'}
              />
            </div>
            <div className={styles.advisorFormMobile}>
              <div className={styles.formHeader}>
                <div className={styles.formTitle}>{`Contact ${title.split(' ')[0]}'s Team`}</div>
                <div className={styles.formBody}>
                  {isNZ ? (
                    'Please fill out the form and we will be in touch.'
                  ) : (
                    <>
                      Please fill out the form and we will be in touch, or phone us on{' '}
                      <a href="tel:1300 856 065">1300 856 065.</a>
                    </>
                  )}
                </div>
              </div>
              <div className={styles.formIncludeMobile}>
                <PhnxHubspotform
                  title={`Contact ${title.split(' ')[0]}'s Team`}
                  formId={
                    isNZLocale
                      ? '2f3e35b1-fa16-4cb7-98c5-596d6ed1e754'
                      : '51e025c4-8223-409e-9520-973f57831d5e'
                  }
                  hiddenFields={{ '0-1/last_submitted_servicing_adviser_name': title }}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
